import React from 'react'
import {useToast as useChakraToast, UseToastOptions} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import {Toast} from '_uiComponents'
import {useIsAssistiveMode} from '_customHooks'

interface UseToastParams extends Omit<UseToastOptions, 'title'> {
    title: string
    actionLabel: string
    productImg?: string
    isUserLogged?: boolean
}

/**
 * @name useToast
 *
 * @hook
 *
 * @description
 * Displays a toast message on the screen.
 *
 * @remarks
 * This is a custom hook to handle showing toasts in the app, preventing duplicate toasts and to add action elements
 * to toasts when required. It supports all props supported by Chakra toast.
 *
 * @param {string} title Message text to be displayed in toast
 * @param {string} id - id provided to the toast to avoid duplicate toast ids, use it if multiple toasts are needed
 * @param {string} status Semantic state of the toast - success | error | info | warning
 * @param {node} action Optional component to be displayed in the toast (eg. Button to allow user to perform action)
 * @param {string} position The placement of the toast on screen
 * @param {number} duration The delay before the toast hides (in milliseconds)
 */
export const useToast = () => {
    const toast = useChakraToast()
    const {formatMessage} = useIntl()
    const isAssistiveMode = useIsAssistiveMode()

    const closeLabel = formatMessage({id: 'alt.link.close', defaultMessage: 'Close'})
    const wishlistLabel = formatMessage({id: 'whislist.confirmation.modal.my.wishlist', defaultMessage: 'My Wishlist'})

    return ({title, status, actionLabel, productImg, position = 'top-right', duration = 2000, isUserLogged}: UseToastParams) => {
        // WCAG 2.2 SC 2.2.1 – When assistive view (html.un-assistive) is active,
        // toasts must persist until the user explicitly dismisses them.
        const resolvedDuration = isAssistiveMode ? null : duration

        let toastConfig: UseToastOptions = {
            title,
            status,
            position,
            duration: resolvedDuration,
            containerStyle: {
                m: 0,
                mb: 'spacing.5',
                maxW: {base: '100%', md: '422px'},
                minW: {base: 'calc(100dvw - 40px)', md: '422px'},
            },
        }

        toastConfig = {
            ...toastConfig,

            render: ({onClose}) => (
                <Toast
                    title={title}
                    productImg={productImg}
                    status={status}
                    wishlistLabel={wishlistLabel}
                    isUserLogged={isUserLogged}
                    onClose={onClose}
                    closeLabel={closeLabel}
                >
                    {actionLabel}
                </Toast>
            ),
        }

        toast(toastConfig)
    }
}
